import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface SelectAllToggleProps {
  /** Whether all items are selected */
  checked?: boolean
  /** Whether some (but not all) items are selected */
  indeterminate?: boolean
  /** Called when the toggle is clicked */
  onToggle?: () => void
  /** Label text */
  label?: string
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled SelectAllToggle sub-component for MultiSelectHeader.
 *
 * Renders a checkbox with "Select all" label.
 * All visual state determined by props.
 */
const SelectAllToggle = React.memo(
  ({
    checked = false,
    indeterminate = false,
    onToggle,
    label = 'Select all',
  }: SelectAllToggleProps) => {
    const [css, theme] = useStyletron()

    const handleClick = () => {
      onToggle?.()
    }

    const containerClassName = css(getContainerStyles())
    const checkboxClassName = css(getCheckboxStyles(theme))
    const labelClassName = css(getLabelStyles(theme))

    return (
      <div className={containerClassName}>
        <input
          type="checkbox"
          checked={checked}
          ref={el => {
            if (el) {
              el.indeterminate = indeterminate && !checked
            }
          }}
          onChange={handleClick}
          className={checkboxClassName}
          aria-label={label}
        />
        <span className={labelClassName} onClick={handleClick}>
          {label}
        </span>
      </div>
    )
  }
)

SelectAllToggle.displayName = 'SelectAllToggle'

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getContainerStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const getCheckboxStyles = (theme: any) => ({
  width: '16px',
  height: '16px',
  cursor: 'pointer',
  accentColor: theme.colors.primary,
})

const getLabelStyles = (theme: any) => ({
  fontSize: '13px',
  color: theme.colors.grey70,
  cursor: 'pointer',
  userSelect: 'none' as const,
})

export default SelectAllToggle
